export interface Responsibility {
  id: string;
  description: string;
}

export interface Commitment {
  description: string;
  duration: string;
}

export interface CompensationItem {
  id: string;
  description: string;
}

export interface QualificationItem {
  id: string;
  description: string;
}

export interface FeeItem {
  id: string;
  description: string;
  amount?: string;
}

export interface Role {
  id: string;
  title: string;
  description: string;
  importantNote: string[];
  isSupervisoryRole: boolean;
  roleDescription: string;
  responsibilities: Responsibility[];
  compensation: CompensationItem[];
  fees?: FeeItem[];
  timeCommitments: Commitment[];
  tenure?: string;
  qualifications: QualificationItem[];
  docLink: string;
}


const commonImportantNotes: string[] = [
  "Since there is no revenue share or salary system, every team member pays for the service they access from the practice; payments happen at the beginning of the month.",
  "Every therapist has their individual private practice, and their clients directly pay their therapist; no middle-man is involved.",
  "Every therapist in the group practice has their supervisor, and are encouraged to stay in regular therapy with their own therapist too.",
];


export const seniorTherapistRole: Role = {
  id: "senior-therapist",
  title: "Senior Therapist",
  description:
    "गृह प्रवेश® Therapy and Healing Centre is launching India's first no-revenue share therapy model to redefine the mental health space to build supportive, non-exploitative communities for therapists, while paving a safe and self-sustaining path for future generations in the field.",
  importantNote: commonImportantNotes,
  isSupervisoryRole: true,
  roleDescription:
    "This is a supervisory role; you will be paid by the supervisees (Associate therapists) and supported by Senior-Track therapist in your team management functions as they shadow you to learn supervisory and team management skills, ways of which is to be mutually decided by you and the senior-track.",
  responsibilities: [
    {
      id: "supervision",
      description:
        "Supervise client cases through regular individual and group supervision to ensure effective therapeutic outcomes and ethical practice.",
    },
    {
      id: "nurture",
      description:
        "Nurture young therapists' talent by helping them develop and grow their reflexivity",
    },
    {
      id: "balance",
      description:
        "Help therapists balance work and life so as to prevent/slow down burnout",
    },
    {
      id: "referrals",
      description: "Refer cases, if they are a good enough match",
    },
    {
      id: "peer-meetings",
      description:
        "Engage in monthly peer meeting(s) with other seniors to exchange notes and stay up-to-date",
    },
    {
      id: "mentorship",
      description:
        "Help Senior-track therapists learn skills of supervision and team management as they help you with your functions",
    },
    {
      id: "feedback",
      description:
        "Provide feedback to the Founding Therapist to ensure continuity fair and accountable practices in the model",
    },
  ],
  compensation: [
    {
      id: "fee-setting",
      description:
        "Supervisors are free to set their charges for three meetings a month: 2 individual supervision meets, and 1 group meeting with their team",
    },
    {
      id: "matching",
      description:
        "We will be enquiring about your charges and match you with relevant Associate therapists who can afford your fee",
    },
    {
      id: "fee-range",
      description:
        "We will be discussing a range of fee that the practice will follow, your recommendation for it will be highly appreciated",
    },
  ],
  timeCommitments: [
    {
      description: "1 group supervision meeting",
      duration: "1.5 hours",
    },
    {
      description: "2 individual supervision meets",
      duration: "1 hour each",
    },
    {
      description: "1 Seniors' meeting",
      duration: "~1.5 hours",
    },
  ],
  qualifications: [
    {
      id: "education",
      description: "Master's degree in Psychology or related field.",
    },
    {
      id: "experience",
      description: "Minimum of 3 years of counseling experience.",
    },
    {
      id: "supervision-exp",
      description:
        "Some previous experience in supervision, mentorship, and team management are a plus.",
    },
    {
      id: "expertise",
      description: "Expertise in one or more therapeutic modalities.",
    },
  ],
  docLink:
    "https://docs.google.com/document/d/1oCyC9nwPyU6VbHFmOnSp-NtP8feODKforPxEtCVeoyk/edit?tab=t.0#heading=h.wtoo042zfvma",
};

export const seniorTrackTherapistRole: Role = {
  id: "senior-track-therapist",
  title: "Senior-Track Therapist",
  description:
    "गृह प्रवेश® Therapy and Healing Centre is launching India's first no-revenue share therapy model to redefine the mental health space to build supportive, non-exploitative communities for therapists, while paving a safe and self-sustaining path for future generations in the field.",
  importantNote: commonImportantNotes,
  isSupervisoryRole: false,
  roleDescription:
    "This is a learning and supportive role; as you shadow Senior Therapists to learn specialized modality skills, supervision and team management, you support them in their role as seniors; hence no direct compensation from any party is involved (though other benefits are outlined in the next section).",
  responsibilities: [
    {
      id: "shadow-senior",
      description:
        "Shadow Senior Therapists to learn specialized modality skills, supervision and team management",
    },
    {
      id: "support-senior",
      description: "Support Senior Therapists in their supervisory roles",
    },
    {
      id: "optional-practice",
      description:
        "Optional learning component of expanding your private practice (to get more clients, diversify offerings, etc.)",
    },
  ],
  compensation: [
    {
      id: "mentorship",
      description:
        "This can be considered a mentorship role wherein you can learn how to diversify your income streams via supervision, team management, group work, branding, etc.",
    },
    {
      id: "new-initiatives",
      description:
        "You can start new programs or initiatives or any other types of offerings, with guidance from your senior(s), with a readily accessible therapist population who can opt-in",
    },
  ],
  timeCommitments: [
    {
      description: "1 group supervision meeting",
      duration: "1.5 hours",
    },
    {
      description: "1 Seniors' meeting",
      duration: "~1.5 hours",
    },
    {
      description: "Time spent supporting the senior",
      duration: "to be decided mutually",
    },
  ],
  qualifications: [
    {
      id: "education",
      description: "Master's degree in Psychology or related field.",
    },
    {
      id: "experience",
      description:
        "2-3 years of counseling experience (ideally, but case-to-case exceptions can be made)",
    },
    {
      id: "interest",
      description:
        "Strong interest in enhancing supervision, mentorship, and team management skills",
    },
  ],
  docLink:
    "https://docs.google.com/document/d/1T0eJub-sV4fmP-Namt0i7VlLTmHin1oLDGhdCiHb3fA/edit?tab=t.0",
};

// Associate Therapist Data
export const associateTherapistRole: Role = {
  id: "associate-therapist",
  title: "Associate Therapist",
  description:
    "गृह प्रवेश® Therapy and Healing Centre is launching India's first no-revenue share therapy model to redefine the mental health space to build supportive, non-exploitative communities for therapists, while paving a safe and self-sustaining path for future generations in the field.",
  importantNote: commonImportantNotes,
  isSupervisoryRole: false,
  roleDescription:
    'This is a learning role, as you come into your own "therapist self" as you develop your skills as a therapist, entrepreneur, and for your brand',
  responsibilities: [
    {
      id: "senior-engagement",
      description:
        "Actively engage with your senior team to develop foundational, process skills",
    },
    {
      id: "practice-building",
      description:
        "Focus on building your private practice, including but not limited to getting your clients, maintaining a healthy caseload, and setting up smooth and automated operations",
    },
    {
      id: "upskilling",
      description: "Engage in monthly upskilling (topic decided as a group)",
    },
    {
      id: "visibility",
      description:
        "Focus on learning and reflecting on increasing visibility and developing a unique standing in the field",
    },
    {
      id: "specialization",
      description:
        "Begin specializing in one school of thought, while you audit others",
    },
  ],
  compensation: [
    {
      id: "direct-charging",
      description:
        "This is a no-revenue share model where you directly charge from the clients; the entire fee that you set for your therapy stays with you",
    },
    {
      id: "safe-therapy",
      description:
        "SafeTherapy has offered a bunch of exclusive benefits to us all, you can read more about it here!",
    },
  ],
  fees: [
    {
      id: "supervision",
      description: "Therapy Supervision - individual and group",
      amount:
        "roughly between 2,500-4,000/- a month, depending on your supervisor and modality preferred; 1000-1500/- for individual supervision and 500-1000/- for group supervision",
    },
    {
      id: "visibility",
      description:
        "Increasing visibility to gain opportunities and clients - Reflecting on our relationship with visibility, understanding obstacles, discovering your strengths and unique style, and learning from industry experts on how to be more visible",
      amount: "750-1000/- a month, pay-as-you-can",
    },
    {
      id: "upskilling",
      description:
        "Monthly Upskilling - Learning something new and latest, in a peer setting once a month",
      amount: "750-1000/- a month, pay-as-you-can",
    },
  ],
  timeCommitments: [
    {
      description: "1 group supervision meeting",
      duration: "1.5 hours",
    },
    {
      description: "2 individual supervision meetings",
      duration: "1 hour each",
    },
    {
      description: "1 monthly upskilling meeting",
      duration: "~1.5 hours",
    },
    {
      description:
        "1 monthly meeting to discuss and reflect on increasing visibility as a therapist",
      duration: "~1.5-2 hours",
    },
  ],
  tenure: "1 Year to start with, to be renewed based on need and fit.",
  qualifications: [
    {
      id: "education",
      description:
        "Master's degree in Psychology or related field (completed or completing in 2025)",
    },
    {
      id: "experience",
      description: "0-2 years of counseling experience.",
    },
    {
      id: "interest",
      description:
        "Interest in creating and continuing their private practice.",
    },
  ],
  docLink:
    "https://docs.google.com/document/d/1__P3NH8iss7MESVjznT6y_TbsKYD3QzVs0Im1B2iIlM/edit?tab=t.0",
};

// Export all roles in a roles array for easy access
export const roles: Role[] = [
  seniorTherapistRole,
  seniorTrackTherapistRole,
  associateTherapistRole,
];

// Helper function to get a role by ID
export const getRoleById = (id: string): Role | undefined => {
  return roles.find((role) => role.id === id);
};

// Helper function to get all roles
export const getAllRoles = (): Role[] => {
  return roles;
};

// Function to get the common goal text
export const getCommonGoalText = (): string => {
  return "The goal is to work in a team without losing what makes us, us.";
};
